// =====================================================
// TRAMAS DE LOS LOTES EN EL MAPA (BDSJEA-463)
// src/components/Mapa/PatronesLote.js
//
// Define, en un <svg> oculto del documento, un <pattern> punteado y uno rayado por cada color de la
// paleta de destinos. Los polígonos de react-leaflet usan `fillColor="url(#id)"` (ver estiloLote en
// constants/usosLote): Leaflet lo copia al atributo `fill` del <path>, y el navegador resuelve la
// referencia en el mismo documento (verificado con Leaflet 1.x). Sin dependencias nuevas.
// Si la trama no se pudiera resolver, el borde conserva el color del destino y el texto de la
// tenencia sigue en el popup / la lista / la leyenda (la información no depende solo de la trama).
// =====================================================

import React from "react"

import {
  PATRON_PUNTEADO,
  PATRON_RAYADO,
  coloresDeLaPaleta,
  etiquetaTenencia,
  idPatronLote,
  TENENCIA_ARRENDADO,
  TENENCIA_PROPIO,
} from "constants/usosLote"

const LADO = 8

/** <pattern> de un color y una trama: fondo tenue + motivo marcado. */
const Patron = ({ patron, color }) => (
  <pattern
    id={idPatronLote(patron, color)}
    width={LADO}
    height={LADO}
    patternUnits="userSpaceOnUse"
    patternTransform={patron === PATRON_RAYADO ? "rotate(45)" : undefined}
  >
    <rect width={LADO} height={LADO} fill={color} fillOpacity="0.28" />
    {patron === PATRON_PUNTEADO ? (
      <circle
        cx={LADO / 2}
        cy={LADO / 2}
        r="1.8"
        fill={color}
        fillOpacity="0.95"
      />
    ) : (
      <line
        x1="0"
        y1="0"
        x2="0"
        y2={LADO}
        stroke={color}
        strokeWidth="3"
        strokeOpacity="0.95"
      />
    )}
  </pattern>
)

/** <svg> oculto con todas las tramas. Se monta UNA vez junto al mapa. */
export const PatronesLote = () => (
  <svg
    width="0"
    height="0"
    style={{ position: "absolute" }}
    aria-hidden="true"
    focusable="false"
    data-testid="patrones-lote"
  >
    <defs>
      {coloresDeLaPaleta().map(color => (
        <React.Fragment key={color}>
          <Patron patron={PATRON_PUNTEADO} color={color} />
          <Patron patron={PATRON_RAYADO} color={color} />
        </React.Fragment>
      ))}
    </defs>
  </svg>
)

// Estilos CSS de las muestras (chips y leyenda): misma trama que el mapa, con gradientes.
const fondoMuestra = (color, patron) => {
  if (patron === PATRON_PUNTEADO) {
    return {
      backgroundColor: `${color}47`,
      backgroundImage: `radial-gradient(circle, ${color} 1.6px, transparent 1.8px)`,
      backgroundSize: `${LADO}px ${LADO}px`,
    }
  }
  if (patron === PATRON_RAYADO) {
    return {
      backgroundColor: `${color}47`,
      backgroundImage: `repeating-linear-gradient(45deg, ${color} 0, ${color} 2px, transparent 2px, transparent 5px)`,
    }
  }
  return { backgroundColor: color }
}

/**
 * Muestra cuadrada de color (destino) + trama (tenencia). Sin trama = color liso.
 * `titulo` es el texto accesible (p. ej. "Agrícola, Propio").
 */
export const MuestraLote = ({
  color,
  patron,
  size = 20,
  titulo,
  aproximado = false,
}) => (
  <span
    role="img"
    aria-label={titulo}
    title={titulo}
    data-testid="muestra-lote"
    data-patron={patron || ""}
    data-aproximado={aproximado ? "true" : "false"}
    style={{
      display: "inline-block",
      width: size,
      height: size,
      // BDSJEA-471: borde punteado y grueso = polígono aproximado (conserva color y trama)
      border: aproximado ? `3px dotted ${color}` : `1px solid ${color}`,
      flexShrink: 0,
      ...fondoMuestra(color, patron),
    }}
  />
)

/** Muestras de la leyenda de tenencia (en gris neutro: la trama es independiente del color). */
export const MUESTRAS_TENENCIA = [
  { codigo: TENENCIA_PROPIO, patron: PATRON_PUNTEADO },
  { codigo: TENENCIA_ARRENDADO, patron: PATRON_RAYADO },
].map(t => ({ ...t, etiqueta: etiquetaTenencia(t.codigo) }))

export default PatronesLote
